import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { COLOR_SCHEME_PREFERENCES } from '../constants/user'

export interface UserSliceState {
  id: number | null
  classroomId?: number | null
  firstName: string | null
  lastName: string | null
  emailAddress: string | null
  role: string | null
  validated: boolean | null
  deleted: boolean | null
  schoolId: number | null
  districtId: number | null
  publicId: string | null
  dailyBeatsPreference: string | null
  canManageAdmins: string | null
  mustAcceptTerms: boolean | null
  summaryOfTermsUpdates: string | null
  coursePoints: number | null
  mheCoursePoints: number | null
  lastSeenStudentInterfaceVersion: number | null
  completedStudentOrientation: boolean | null
  showCourseProgressResetNotice: boolean | null
  completedTeacherOrientation: boolean | null
  dismissedStudentViewCallout: boolean | null
  seenWelcomeToStudentView: boolean | null
  teacherVerificationStatus: string | null
  seenAgeAndConsentDisclaimerStatus: string | null
  seenImportantInfoAboutNewClass: boolean | null
  seenTenDayCourseTabInfo: boolean | null
  dismissedArchivingClassesCallout: boolean | null
  showAutoArchivedClassesNotice: boolean | null
  dismissedAISupportFeatureTag: boolean | null
  seenClassLinkingTeacherInfo: boolean | null
  seenFocusSeshPreSetupOrientation: boolean | null
  seenFocusSeshPreSeshOrientation: boolean | null
  seenFocusSeshPreSeshOrientationStatus: string | null
  showVoiceGuidanceReturningUserDialog: boolean | null
  voiceGuidanceCurriculumIndex: number | null
  showVoiceGuidanceCaptions: boolean | null
  seenDisableVoiceGuidancePopUp?: boolean | null
  showCompletionGraph: boolean | null
  recentSoundtracks: string[] | null
  dismissedFocusCoachConfidentialityMessage: boolean | null
  dismissedPiPFeatureTag: boolean | null
  dismissedPersonalReflectionConfidentialityMessage: boolean | null
  autoCheckInsFrequencyPreferenceLevel: number | null
  showAutoCheckInsPostSeshFeedback: boolean | null
  lastCustomFocusSeshWasTimed: boolean
  lastCustomFocusSeshDuration: number | null
  lastCustomFocusSeshWasNoCheckIns: boolean | null
  lastCustomFocusSeshWasAutoCheckIns: boolean | null
  lastCustomFocusSeshCheckInTiming: string | null
  lastCustomFocusSeshCheckInSound: string | null
  lastCustomFocusSeshSoundtrack: string | null
  lastCustomFocusSeshVoiceGuidance: string | null
  totalCoursePoints: number | null
  pointsPerLessonCompletion: number | null
  pointsPerDailyBeatCompletion: number | null
  colorSchemePreference: string
  experimentalFeaturesEnabled: boolean | null
  focusStreakCount: number | null
  lessonNarrationEnabled: boolean | null
  lessonSoundtrackEnabled: boolean | null
}

export const selectDailyBeatsPreference = (state: any) => state.user.dailyBeatsPreference
export const selectUser = (state: any) => state.user
export const selectUserRole = (state: any) => state.user?.role
export const selectRecentSoundtracks = (state: any) => state.user.recentSoundtracks
export const selectPersonalViewClassroomId = (state: any) => ['personal', 'student', 'admin'].includes(state.user.role) ? null : state.user.classroomId || 0

export const SEEN_FOCUS_SESH_PRE_SESH_ORIENTATION_STATUS = {
  HAS_NOT_SEEN: 'has_not_seen',
  NATIVE: 'native',
  WEB: 'web',
  BOTH: 'both',
} as const

export const SEEN_AGE_AND_CONSENT_DISCLAIMER_STATUS = {
  NOT_APPLICABLE: 'not_applicable',
  HAS_NOT_SEEN: 'has_not_seen',
  HAS_SEEN: 'has_seen',
} as const

export const MAX_NUMBER_OF_RECENT_SOUNDTRACKS = 4

export const MIN_AUTO_CHECK_INS_FREQUENCY_PREFERENCE_LEVEL = 1
export const MAX_AUTO_CHECK_INS_FREQUENCY_PREFERENCE_LEVEL = 7

const initialState: UserSliceState = {
  id: null,
  firstName: null,
  lastName: null,
  emailAddress: null,
  role: null,
  validated: null,
  deleted: null,
  schoolId: null,
  districtId: null,
  publicId: null,
  dailyBeatsPreference: null,
  canManageAdmins: null,
  mustAcceptTerms: null,
  summaryOfTermsUpdates: null,
  coursePoints: null,
  mheCoursePoints: null,
  lastSeenStudentInterfaceVersion: null,
  completedStudentOrientation: null,
  showCourseProgressResetNotice: null,
  completedTeacherOrientation: null,
  dismissedStudentViewCallout: null,
  seenWelcomeToStudentView: null,
  teacherVerificationStatus: null,
  seenAgeAndConsentDisclaimerStatus: null,
  seenImportantInfoAboutNewClass: null,
  seenTenDayCourseTabInfo: null,
  dismissedArchivingClassesCallout: null,
  showAutoArchivedClassesNotice: null,
  dismissedAISupportFeatureTag: null,
  seenClassLinkingTeacherInfo: null,
  seenFocusSeshPreSetupOrientation: null,
  seenFocusSeshPreSeshOrientation: null,
  seenFocusSeshPreSeshOrientationStatus: null,
  showVoiceGuidanceReturningUserDialog: null,
  voiceGuidanceCurriculumIndex: null,
  showVoiceGuidanceCaptions: null,
  showCompletionGraph: null,
  recentSoundtracks: null,
  dismissedFocusCoachConfidentialityMessage: null,
  dismissedPiPFeatureTag: null,
  dismissedPersonalReflectionConfidentialityMessage: null,
  autoCheckInsFrequencyPreferenceLevel: null,
  showAutoCheckInsPostSeshFeedback: null,
  lastCustomFocusSeshWasTimed: true,
  lastCustomFocusSeshDuration: null,
  lastCustomFocusSeshWasNoCheckIns: null,
  lastCustomFocusSeshWasAutoCheckIns: null,
  lastCustomFocusSeshCheckInTiming: null,
  lastCustomFocusSeshCheckInSound: null,
  lastCustomFocusSeshSoundtrack: null,
  lastCustomFocusSeshVoiceGuidance: null,
  totalCoursePoints: null,
  pointsPerLessonCompletion: null,
  pointsPerDailyBeatCompletion: null,
  colorSchemePreference: COLOR_SCHEME_PREFERENCES.SYSTEM,
  experimentalFeaturesEnabled: null,
  focusStreakCount: null,
  lessonNarrationEnabled: null,
  lessonSoundtrackEnabled: null,
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loadUser: (state, {
      payload: {
        id,
        classroomId,
        firstName,
        lastName,
        emailAddress,
        role,
        validated,
        deleted,
        schoolId,
        districtId,
        publicId,
        dailyBeatsPreference,
        canManageAdmins,
        mustAcceptTerms,
        summaryOfTermsUpdates,
        showCourseProgressResetNotice,
        completedTeacherOrientation,
        dismissedStudentViewCallout,
        seenWelcomeToStudentView,
        coursePoints,
        mheCoursePoints,
        totalCoursePoints,
        pointsPerLessonCompletion,
        pointsPerDailyBeatCompletion,
        colorSchemePreference,
        teacherVerificationStatus,
        seenFocusSeshPreSetupOrientation,
        seenFocusSeshPreSeshOrientation,
        seenFocusSeshPreSeshOrientationStatus,
        showVoiceGuidanceReturningUserDialog,
        voiceGuidanceCurriculumIndex,
        showVoiceGuidanceCaptions,
        showCompletionGraph,
        recentSoundtracks,
        dismissedFocusCoachConfidentialityMessage,
        dismissedPiPFeatureTag,
        dismissedPersonalReflectionConfidentialityMessage,
        autoCheckInsFrequencyPreferenceLevel,
        showAutoCheckInsPostSeshFeedback,
        seenAgeAndConsentDisclaimerStatus,
        lastSeenStudentInterfaceVersion,
        completedStudentOrientation,
        seenImportantInfoAboutNewClass,
        seenTenDayCourseTabInfo,
        dismissedArchivingClassesCallout,
        showAutoArchivedClassesNotice,
        dismissedAISupportFeatureTag,
        seenClassLinkingTeacherInfo,
        lastCustomFocusSeshWasTimed,
        lastCustomFocusSeshDuration,
        lastCustomFocusSeshWasNoCheckIns,
        lastCustomFocusSeshWasAutoCheckIns,
        lastCustomFocusSeshCheckInTiming,
        lastCustomFocusSeshCheckInSound,
        lastCustomFocusSeshSoundtrack,
        lastCustomFocusSeshVoiceGuidance,
        experimentalFeaturesEnabled,
        focusStreakCount,
        lessonNarrationEnabled,
        lessonSoundtrackEnabled,
      },
    }: PayloadAction<any>) => ({
      id,
      classroomId,
      firstName,
      lastName,
      emailAddress,
      role,
      validated,
      deleted,
      schoolId,
      districtId,
      publicId,
      dailyBeatsPreference,
      canManageAdmins,
      mustAcceptTerms,
      summaryOfTermsUpdates,
      showCourseProgressResetNotice,
      completedTeacherOrientation,
      dismissedStudentViewCallout,
      seenWelcomeToStudentView,
      coursePoints,
      mheCoursePoints,
      totalCoursePoints,
      pointsPerDailyBeatCompletion,
      pointsPerLessonCompletion,
      colorSchemePreference,
      teacherVerificationStatus,
      seenFocusSeshPreSetupOrientation,
      seenFocusSeshPreSeshOrientation,
      seenFocusSeshPreSeshOrientationStatus,
      showVoiceGuidanceReturningUserDialog,
      voiceGuidanceCurriculumIndex,
      showVoiceGuidanceCaptions,
      showCompletionGraph,
      recentSoundtracks,
      dismissedFocusCoachConfidentialityMessage,
      dismissedPiPFeatureTag,
      dismissedPersonalReflectionConfidentialityMessage,
      autoCheckInsFrequencyPreferenceLevel,
      showAutoCheckInsPostSeshFeedback,
      seenAgeAndConsentDisclaimerStatus,
      lastSeenStudentInterfaceVersion,
      completedStudentOrientation,
      seenImportantInfoAboutNewClass,
      seenTenDayCourseTabInfo,
      dismissedArchivingClassesCallout,
      showAutoArchivedClassesNotice,
      dismissedAISupportFeatureTag,
      seenClassLinkingTeacherInfo,
      lastCustomFocusSeshWasTimed,
      lastCustomFocusSeshDuration,
      lastCustomFocusSeshWasNoCheckIns,
      lastCustomFocusSeshWasAutoCheckIns,
      lastCustomFocusSeshCheckInTiming,
      lastCustomFocusSeshCheckInSound,
      lastCustomFocusSeshSoundtrack,
      lastCustomFocusSeshVoiceGuidance,
      experimentalFeaturesEnabled,
      focusStreakCount,
      lessonNarrationEnabled,
      lessonSoundtrackEnabled,
    }),
    updateDailyBeatsPreference: (state, { payload: { dailyBeatsPreference } }: PayloadAction<{ dailyBeatsPreference: string }>) => {
      state.dailyBeatsPreference = dailyBeatsPreference
    },
    updateUserName: (state, { payload: { firstName, lastName } }: PayloadAction<{ firstName: string; lastName: string }>) => {
      state.firstName = firstName
      state.lastName = lastName
    },
    updatePersonalUserRoleToStudent: (state) => {
      if (state.role !== 'personal') return

      state.role = 'student'
    },
    updateUserColorSchemePreference: (state, { payload: { preference } }: PayloadAction<{ preference: string }>) => {
      state.colorSchemePreference = preference
    },
    setSeenFocusSeshPreSetupOrientation: (state, { payload: { hasSeen } }: PayloadAction<{ hasSeen: boolean }>) => {
      state.seenFocusSeshPreSetupOrientation = hasSeen
    },
    setSeenFocusSeshPreSeshOrientationStatus: (state, { payload: { status } }: PayloadAction<{ status: string }>) => {
      state.seenFocusSeshPreSeshOrientationStatus = status
    },
    updateShowCompletionGraph: (state, { payload: { showGraph } }: PayloadAction<{ showGraph: boolean }>) => {
      state.showCompletionGraph = showGraph
    },
    setLastSeenStudentInterfaceVersion: (state, { payload: { version } }: PayloadAction<{ version: number }>) => {
      state.lastSeenStudentInterfaceVersion = version
    },
    updateRecentSoundtracksInState: (state, { payload: { newSoundtrack } }: PayloadAction<{ newSoundtrack: string }>) => {
      const previousRecentSoundtracks = state.recentSoundtracks || []
      const newRecentSoundtracks = [
        newSoundtrack,
        ...previousRecentSoundtracks.filter((s) => s !== newSoundtrack),
      ].slice(0, MAX_NUMBER_OF_RECENT_SOUNDTRACKS)
      state.recentSoundtracks = newRecentSoundtracks
    },
    updateDismissedPiPFeatureTag: (state) => {
      state.dismissedPiPFeatureTag = true
    },
    updateAutoCheckInsFrequencyPreferenceLevel: (state, { payload: { level, showAutoCheckInsPostSeshFeedback } }: PayloadAction<{ level: number; showAutoCheckInsPostSeshFeedback: boolean }>) => {
      state.autoCheckInsFrequencyPreferenceLevel = level
      state.showAutoCheckInsPostSeshFeedback = showAutoCheckInsPostSeshFeedback
    },
    updateLastCustomFocusSeshSettings: (state, { payload: newSettings }: PayloadAction<{ isTimed: boolean; duration?: number | null; isNoCheckIns: boolean; isAutoCheckIns: boolean; checkInTiming?: string | null; checkInSound?: string | null; soundtrack: string | null; voiceGuidance: string | null }>) => {
      const {
          isTimed,
          duration,
          isNoCheckIns,
          isAutoCheckIns,
          checkInTiming,
          checkInSound,
          soundtrack,
          voiceGuidance,
      } = newSettings
      state.lastCustomFocusSeshWasTimed = isTimed
      if (duration) {
        state.lastCustomFocusSeshDuration = duration
      }
      state.lastCustomFocusSeshWasNoCheckIns = isNoCheckIns
      state.lastCustomFocusSeshWasAutoCheckIns = isAutoCheckIns
      // Only save new checkInTiming if selected custom timing
      if (!isAutoCheckIns && !isNoCheckIns) {
        state.lastCustomFocusSeshCheckInTiming = checkInTiming
      }
      if (checkInSound) {
        state.lastCustomFocusSeshCheckInSound = checkInSound
      }
      state.lastCustomFocusSeshSoundtrack = soundtrack
      state.lastCustomFocusSeshVoiceGuidance = voiceGuidance
    },
    setCompletedStudentOrientation: (state) => {
      state.completedStudentOrientation = true
    },
    dismissAutoArchivedClassesNotice: (state) => {
      state.showAutoArchivedClassesNotice = false
    },
    dismissCourseProgressResetNotice: (state) => {
      state.showCourseProgressResetNotice = false
    },
    completeTeacherOrientation: (state) => {
      state.completedTeacherOrientation = true
    },
    dismissPersonalReflectionConfidentialityMessage: (state) => {
      state.dismissedPersonalReflectionConfidentialityMessage = true
    },
    setSeenClassLinkingTeacherInfo: (state) => {
      state.seenClassLinkingTeacherInfo = true
    },
    incrementVoiceGuidanceCurriculumIndex: (state) => {
      state.voiceGuidanceCurriculumIndex++
    },
    updateShowVoiceGuidanceCaptions: (state, { payload: { showCaptions } }: PayloadAction<{ showCaptions: boolean }>) => {
      state.showVoiceGuidanceCaptions = showCaptions
    },
    // NOTE: this should only be used for returning users selecting a voice for the first time,
    //  otherwise update focusSeshSlice directly
    updateLastCustomFocusSeshVoiceGuidance: (state, { payload: { voiceGuidance } }: PayloadAction<{ voiceGuidance: string | null }>) => {
      state.lastCustomFocusSeshVoiceGuidance = voiceGuidance
    },
    dismissVoiceGuidanceReturningUserDialog: (state) => {
      state.showVoiceGuidanceReturningUserDialog = false
    },
    incrementFocusStreakCount: (state) => {
      state.focusStreakCount++
    },
    toggleLessonNarration: (state) => {
      state.lessonNarrationEnabled = !state.lessonNarrationEnabled
    },
    toggleLessonSoundtrack: (state) => {
      state.lessonSoundtrackEnabled = !state.lessonSoundtrackEnabled
    },
  },
})

export const {
  loadUser,
  updateDailyBeatsPreference,
  updateUserName,
  updatePersonalUserRoleToStudent,
  updateUserColorSchemePreference,
  setSeenFocusSeshPreSeshOrientationStatus,
  setSeenFocusSeshPreSetupOrientation,
  updateShowCompletionGraph,
  setLastSeenStudentInterfaceVersion,
  updateRecentSoundtracksInState,
  updateDismissedPiPFeatureTag,
  updateAutoCheckInsFrequencyPreferenceLevel,
  updateLastCustomFocusSeshSettings,
  setCompletedStudentOrientation,
  dismissAutoArchivedClassesNotice,
  dismissCourseProgressResetNotice,
  completeTeacherOrientation,
  dismissPersonalReflectionConfidentialityMessage,
  setSeenClassLinkingTeacherInfo,
  incrementVoiceGuidanceCurriculumIndex,
  updateShowVoiceGuidanceCaptions,
  updateLastCustomFocusSeshVoiceGuidance,
  dismissVoiceGuidanceReturningUserDialog,
  incrementFocusStreakCount,
  toggleLessonNarration,
  toggleLessonSoundtrack,
} = userSlice.actions
export default userSlice.reducer
